import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// Service
import { BroadcastService } from '@cv-invision/core';
import { ViewsService } from '../../../../../services/views.service';
// Model
import { BroadcastEventModel } from '@cv-invision/core';
import { FORM_EVENT, PHOTO_SELECT_STATUS } from '../../../../../models/enums.model';
import { SelectPhotoResponse } from '../../../../../models/views-service.model';
import { LayoutRenderLayoutItem } from '../../../models/cv-layout-render.model';
import { SingleImageOptions, SingleImageFilter } from '../../models/components.options';
import { Lightbox } from 'ngx-lightbox';

@Component({
	selector: 'cv-schema-render-single-image',
	templateUrl: './single-image.component.html',
})
export class SingleImageComponent implements OnInit, OnDestroy {

	@Input() data: any;
	@Input() layoutNode = new LayoutRenderLayoutItem();
	options = new SingleImageOptions();

	thisData: any;
	photos: any;
	path: string | null;
	imageLoaded = false;
	imageUrl = null;
	hasSketch = false;
	album = [];
	selected = false;

	get flattenedPhotos(): any {
		return this.viewsService.flattenedPhotos;
	}

	get photosSelected(): any {
		return this.viewsService.photosSelected;
	}

	private interval;
	private atComponentDestroy = new Subject();

	constructor(
		protected broadcastService: BroadcastService,
		protected viewsService: ViewsService,
		private lightbox: Lightbox
	) {
		this.broadcastService
			.onEvent(FORM_EVENT.PHOTO_SELECT_ALL_NONE)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {
				this.selectPhoto();
			});

		this.broadcastService
			.onEvent(FORM_EVENT.PHOTO_SELECT)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {
				if ('jsonPath' in broadcast.event && broadcast.event.jsonPath === this.path) {
					const photoId = broadcast.event.data.id;
					const index = this.album.findIndex(img => img.id === photoId);

					if (index > -1) {
						this.updateAlbum(broadcast.event.data);
					}
				}
			});
	}

	ngOnInit(): void {
		this.options = { ...this.options, ...this.layoutNode.options };

		if (this.data) {
			if (this.options.photosInData) {
				this.photos = this.data.photos;
				this.thisData = this.data.data;
			} else {
				// calculate photos given the path in data
				this.thisData = this.data;
			}

			this.path = this.thisData[this.options.key];
			this.initComponent();
		}

	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();

		if (this.interval) {
			clearInterval(this.interval);
		}
	}

	initComponent(): void {
		let allPhotos = this.flattenedPhotos[this.path] || [];

		if (this.options.photosInData) {
			allPhotos = this.photos;
		}

		if ('filter' in this.options) {
			this.options.filter.every((filt: SingleImageFilter) => {
				const photo = allPhotos.filter(photo => {
					const filterPath = filt.key.split('.');
					let filterData = photo;

					filterPath.forEach(filterKey => {
						if (filterKey in filterData) {
							filterData = filterData[filterKey];
						}
					});

					return (filterData || '') === filt.value.toUpperCase();
				});

				// Stop if image is found
				if (photo.length) {
					allPhotos = photo;
					return false;
				}

				return true;
			});
		}

		if (allPhotos && allPhotos.length) {
			this.addImageToAlbum(allPhotos[0]);
			this.imageUrl = allPhotos[0].thumbnails[0];
			this.hasSketch = true;
		} else {
			this.imageUrl = 'assets/img/placeholder/No-file-asset.png';
		}
	}

	/**
	 * Callback after image is loaded
	 */
	onImageLoad(): void {
		this.imageLoaded = true;
		if (this.interval) {
			clearInterval(this.interval);
		}
	}

	/**
	 * Callback for error on the image, to prevent displaying the img
	 *
	 * @param oldImageUrl
	 */
	onImageError(oldImageUrl: string): void {
		this.imageUrl = null;
		if (this.interval) {
			clearInterval(this.interval);
		}

		this.imageLoaded = false;
		this.interval = setInterval(() => {
			this.imageUrl = oldImageUrl;
		}, 1000);
	}

	/**
	 * Open NGX Gallery
	 */
	openPreview(): void {
		if (this.hasSketch) {
			this.lightbox.open(this.album, 0);
		}
		//this.onlyPreviewGallery.openPreview(0);
	}

	/**
	 * Select a smartag in the list
	 */
	selectImage(triggerBroadcast?: boolean) {
		const photoSelected = this.album[0];
		const photoStatus = this.viewsService.selectPhoto(photoSelected.id);

		this.updateAlbum(photoStatus);

		if (triggerBroadcast) {
			this.broadcastService.broadcast({
				name: FORM_EVENT.PHOTO_SELECT,
				event: {
					data: photoStatus,
					jsonPath: this.path
				}
			});
		}
	}

	/**
	 * Update the album from the broadcast
	 * @param photoStatus
	 */
	private updateAlbum(photoStatus: SelectPhotoResponse): void {
		this.selected = false;

		if (photoStatus.status === PHOTO_SELECT_STATUS.SELECTED) {
			this.selected = true;
		}
	}

	/**
	 * Broadcast to select all or none photos
	 */
	private selectPhoto() {
		const photo = this.album[0];
		let photoindex = -1;

		if (photo) {
			photoindex = this.photosSelected.indexOf(photo.id);
		}

		photoindex > -1 ? this.selected = true : this.selected = false
	}

	/**
	 * Add image object to class variable album
	 * @param img
	 */
	private addImageToAlbum(img: any): void {
		if (img) {

			const image = {
				src: img.path,
				//caption: img.path,
				thumb: img.thumbnails[0],
				id: img.id,
				big: img.path,
				small: img.thumbnails[0],
				medium: img.thumbnails[0],
				description: '',
				showDelete: img.custom,
				refJsonPath: img.refJsonPath
			};
			this.album.push(image);
		}
	}

}
